/**
 * API Service Layer
 * 
 * Centralized HTTP client with auth handling for the SealVision API.
 */

const BASE_URL = '';

interface ApiError {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  errors?: Array<{ field: string; message: string }>;
}

class ApiClient {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { headers?: Record<string, string> },
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...options?.headers,
    };

    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (response.status === 401) {
      // Redirect to login if unauthorized
      if (!path.startsWith('/auth/') && !window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
      }
      throw new Error('Authentication required');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const error: ApiError = data || {
        type: '/errors/unknown',
        title: 'Error',
        status: response.status,
        detail: 'An unexpected error occurred',
        code: 'UNKNOWN',
      };
      throw error;
    }

    return data as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  upload<T>(path: string, formData: FormData): Promise<T> {
    return this.request<T>('POST', path, formData);
  }
}

export const api = new ApiClient();

// ─── Typed API Methods ────────────────────────────────────

export interface UserInfo {
  authenticated: boolean;
  user?: {
    id: string;
    email: string | null;
    name: string | null;
    role: string;
    organizationId: string | null;
  };
  subscription?: {
    id: string;
    status: string;
    priceModelId: string | null;
    currentPeriodEnd: string | null;
    trialEnd: string | null;
  } | null;
}

export interface ProductionLine {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  packagingProcess: string;
  targetSpeedPpm: number;
  status: string;
  createdAt: string;
  stats?: {
    totalInspections: number;
    passCount: number;
    failCount: number;
    warningCount: number;
    passRate: number;
    avgConfidence: number;
    lastInspectionAt: string | null;
  };
  devices?: Array<{
    id: string;
    name: string;
    status: string;
    lastHeartbeatAt: string | null;
  }>;
}

export interface Inspection {
  id: string;
  productionLineId: string;
  result: 'pass' | 'fail' | 'warning';
  confidenceScore: number | null;
  defectType: string | null;
  defectLocation: {
    x: number;
    y: number;
    width: number;
    height: number;
    severity: number;
  } | null;
  thermalMetrics: {
    avgTemperature: number;
    maxTemperature: number;
    minTemperature: number;
    stdDeviation: number;
    coolingRate: number;
    anomalyScore: number;
  } | null;
  thermalImagePath: string | null;
  rgbImagePath: string | null;
  lineSpeedPpm: number | null;
  packageIndex: number | null;
  inspectedAt: string;
}

export interface DashboardData {
  needsOnboarding: boolean;
  hasAccess: boolean;
  subscription: UserInfo['subscription'] | null;
  overview?: {
    totalLines: number;
    activeLines: number;
    totalInspections: number;
    totalFails: number;
    totalPass: number;
    overallPassRate: number;
  };
  lines?: ProductionLine[];
  recentFailures?: Inspection[];
  recentBatches?: Array<{
    id: string;
    batchCode: string;
    productName: string | null;
    status: string;
    startedAt: string;
  }>;
}

// API method helpers
export const authApi = {
  me: () => api.get<UserInfo>('/auth/me'),
};

export const dashboardApi = {
  get: () => api.get<DashboardData>('/api/dashboard'),
  subscription: () => api.get('/api/dashboard/subscription'),
};

export const linesApi = {
  list: () => api.get<ProductionLine[]>('/api/lines'),
  get: (id: string) => api.get(`/api/lines/${id}`),
  create: (data: { name: string; code: string; packagingProcess: string; targetSpeedPpm: number }) =>
    api.post<ProductionLine>('/api/lines', data),
  updateStatus: (id: string, status: string) =>
    api.patch(`/api/lines/${id}/status`, { status }),
  delete: (id: string) => api.delete(`/api/lines/${id}`),
  createDevice: (lineId: string, data: { name: string; model?: string }) =>
    api.post(`/api/lines/${lineId}/devices`, data),
  getDevices: (lineId: string) => api.get(`/api/lines/${lineId}/devices`),
};

export const inspectionsApi = {
  list: (params: { lineId: string; result?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams({ lineId: params.lineId });
    if (params.result) searchParams.set('result', params.result);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    return api.get<{ inspections: Inspection[]; total: number }>(
      `/api/inspections?${searchParams.toString()}`
    );
  },
  get: (id: string, lineId: string) => api.get(`/api/inspections/${id}?lineId=${lineId}`),
  lineStats: (lineId: string, hours?: number) => {
    const params = hours ? `?hours=${hours}` : '';
    return api.get(`/api/inspections/stats/line/${lineId}${params}`);
  },
};

export const batchesApi = {
  list: (limit?: number) => api.get(`/api/batches${limit ? `?limit=${limit}` : ''}`),
  get: (id: string) => api.get(`/api/batches/${id}`),
  updateStatus: (id: string, status: string) =>
    api.patch(`/api/batches/${id}/status`, { status }),
};

export const onboardingApi = {
  status: () => api.get('/api/onboarding/status'),
  complete: (data: { organizationName: string; industry?: string }) =>
    api.post('/api/onboarding', data),
};

export interface BillingData {
  hasSubscription: boolean;
  hasAccess: boolean;
  subscription: {
    id: string;
    status: string;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    trialEnd?: string | null;
    canceledAt?: string | null;
    currency?: string;
    priceModel?: {
      id: string;
      name: string;
      modelType: string;
      amount?: number;
      interval?: string;
      intervalCount?: number;
      tiers?: Array<{
        up_to: number;
        unit_amount?: number;
        unit_amount_decimal?: number;
        flat_amount?: number;
      }>;
    } | null;
  } | null;
  balance?: {
    balance: number;
  } | null;
  marketplaceUrl: string;
  message?: string;
}

export const billingApi = {
  get: () => api.get<BillingData>('/api/billing'),
  sync: () => api.post<BillingData>('/api/billing/sync'),
};

